<template>
    <div class="grid">
        <div class="col-12">
            <Toast />
            <ConfirmDialog />

            <!-- Barra superior: título, vista, config del salón + pestañas de zonas -->
            <div class="rt-topbar card">
                <div class="rt-topbar-row">
                    <div class="rt-title">
                        <i class="pi pi-th-large"></i>
                        <h4 class="m-0">Salón · {{ activityName }}</h4>
                    </div>
                    <div class="rt-actions-top">
                        <SelectButton v-model="view" :options="viewOptions" optionValue="value" dataKey="value"
                                      :unselectable="false" aria-label="Vista del salón">
                            <template #option="{ option }">
                                <i :class="option.icon"></i><span class="rt-view-label">{{ option.label }}</span>
                            </template>
                        </SelectButton>
                        <template v-if="canManage">
                            <template v-if="manageMode">
                                <Button label="Zonas" icon="pi pi-th-large" class="p-button-outlined"
                                        @click="zoneManagerVisible = true" />
                                <Button label="Nueva mesa" icon="pi pi-plus" class="p-button-outlined" @click="newTable" />
                                <Button v-if="dirty" label="Descartar" class="p-button-text" @click="discardLayout" />
                                <Button :label="dirty ? 'Guardar y salir' : 'Listo'" icon="pi pi-check"
                                        class="p-button-success" :loading="savingLayout" @click="finishManage" />
                            </template>
                            <Button v-else label="Configurar salón" icon="pi pi-cog" class="p-button-outlined"
                                    @click="startManage" />
                        </template>
                    </div>
                </div>

                <div v-if="tabs.length && zonesEnabled" class="rt-zones" role="tablist" aria-label="Zonas del salón">
                    <button v-for="z in tabsWithAll" :key="z.id" type="button" role="tab" class="rt-zone"
                            :aria-selected="z.id === currentZoneId" :class="{ 'rt-zone-on': z.id === currentZoneId }"
                            @click="selectedZoneId = z.id">
                        {{ z.name }} <span class="rt-zone-count">{{ z.tableCount }}</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Salón -->
        <div class="col-12" :class="{ 'lg:col-7': selectedTableId, 'xl:col-8': selectedTableId }">
            <div class="card">
                <div v-if="manageMode" class="rt-manage-hint">
                    <i class="pi pi-info-circle"></i>
                    <span><b>Arrastra</b> las mesas para ubicarlas como están en tu local (o usa las flechas).
                        <b>Toca</b> una para editarla.<template v-if="dirty"> · <b>{{ dirtyCount }}</b> sin guardar</template></span>
                </div>
                <div class="rt-legend">
                    <span><i class="rt-dot rt-dot-free"></i> Libre</span>
                    <span><i class="rt-dot rt-dot-occupied"></i> Ocupada</span>
                    <span><i class="rt-dot rt-dot-billing"></i> Por cobrar</span>
                    <span><i class="rt-dot rt-dot-reserved"></i> Reservada</span>
                    <span class="rt-legend-bubble"><i class="rt-bubble-sample">2</i> nº de cuentas</span>
                </div>

                <div v-if="loading && !loaded" class="rt-floor-empty">
                    <i class="pi pi-spin pi-spinner" style="font-size: 1.5rem;"></i>
                </div>

                <!-- Sin zonas ni mesas: primer uso -->
                <div v-else-if="!tabs.length" class="rt-floor-empty">
                    <i class="pi pi-map" style="font-size: 2rem;"></i>
                    <p v-if="canManage">Empieza creando las zonas de tu local (Salón, Terraza…) y luego sus mesas.</p>
                    <p v-else>Aún no hay mesas configuradas. Pídele al administrador que arme el salón.</p>
                    <Button v-if="canManage" label="Crear zonas" icon="pi pi-th-large"
                            @click="startManage(); zoneManagerVisible = true" />
                </div>

                <template v-else>
                    <div v-if="!sections.length" class="rt-floor-empty">
                        <p>Aún no hay mesas en el salón.</p>
                        <Button v-if="canManage" label="Nueva mesa" icon="pi pi-plus" @click="newTable" />
                    </div>

                    <!-- Una zona, o «Todas»: cada zona como sección, una debajo de otra -->
                    <section v-for="s in sections" :key="s.id" class="rt-section">
                        <button v-if="isAll" type="button" class="rt-section-title" :title="'Ver solo ' + s.name"
                                @click="selectedZoneId = s.id">
                            {{ s.name }}
                            <span>{{ s.tables.length }} {{ s.tables.length === 1 ? 'mesa' : 'mesas' }}</span>
                            <i class="pi pi-angle-right"></i>
                        </button>
                        <FloorMap v-if="view === 'map'" :tables="s.tables" :canvas="canvas" :selectedId="selectedTableId"
                                  :editable="manageMode" :crop="isAll" :showTotals="showTotals"
                                  @select="onTileClick" @drag-end="onDragEnd">
                            <div v-if="!s.tables.length" class="rt-plane-empty">
                                <p>Esta zona no tiene mesas.</p>
                                <Button v-if="canManage" label="Nueva mesa" icon="pi pi-plus" @click="newTable(s.id)" />
                            </div>
                        </FloorMap>

                        <div v-else-if="s.tables.length" class="rt-grid">
                            <button v-for="t in s.tables" :key="t.id" type="button" class="rt-tile"
                                    :class="['rt-state-' + t.state, { 'rt-tile-selected': t.id === selectedTableId,
                                                                        'rt-tile-manage': manageMode }]"
                                    @click="onTileClick(t)">
                                <div class="rt-tile-top">
                                    <span class="rt-tile-name">{{ t.name }}</span>
                                    <span v-if="manageMode" class="rt-tile-edit"><i class="pi pi-pencil"></i></span>
                                    <span v-else-if="t.state !== 'free'" class="rt-tile-badge">{{ stateLabel(t.state) }}</span>
                                </div>
                                <div class="rt-tile-mid">
                                    <span v-if="t.capacity" class="rt-tile-cap">{{ t.capacity }} pers</span>
                                    <span v-if="t.accountCount" class="rt-tile-bubble">{{ t.accountCount }}
                                        {{ t.accountCount === 1 ? 'cuenta' : 'cuentas' }}</span>
                                </div>
                                <div class="rt-tile-bottom">
                                    <span v-if="t.state !== 'free' && showTotals" class="rt-tile-total">${{ money(t.total) }}</span>
                                </div>
                            </button>
                        </div>
                        <div v-else class="rt-floor-empty">
                            <p>Esta zona no tiene mesas.</p>
                            <Button v-if="canManage" label="Nueva mesa" icon="pi pi-plus" @click="newTable(s.id)" />
                        </div>
                    </section>
                </template>
            </div>
        </div>

        <!-- Panel de la mesa -->
        <div v-if="selectedTableId" class="col-12 lg:col-5 xl:col-4">
            <TableDetailPanel ref="panel" :tableId="selectedTableId" :tables="floor.tables" :zones="floor.zones"
                              :canvas="canvas" :kitchenEnabled="sendToKitchen" :splitPayment="splitPayment"
                              @changed="loadFloor" @open-account="onOpenAccount"
                              @add-items="onAddItems" @close="selectedTableId = null" />
        </div>

        <ProductPickerDialog v-model:visible="pickerVisible" :askLabel="pickerMode === 'open'"
                             :title="pickerTitle" :confirmLabel="pickerMode === 'open' ? 'Abrir cuenta' : 'Agregar'"
                             :saving="pickerSaving" @confirm="onPickerConfirm" />

        <TableFormDialog v-model:visible="tableFormVisible" :table="editingTable" :zones="floor.zones"
                         :defaultZoneId="newTableZoneId"
                         @saved="onTableSaved" @deleted="onTableDeleted" />

        <ZoneManagerDialog v-model:visible="zoneManagerVisible" :zones="tabsOfRealZones"
                           @changed="loadFloor" @created="z => (selectedZoneId = z.id)" />
    </div>
</template>

<script>
import httpRequest from '../../service/httpRequest';
import AppEventBus from '@/AppEventBus';
import TableDetailPanel from './TableDetailPanel.vue';
import ProductPickerDialog from './ProductPickerDialog.vue';
import TableFormDialog from './TableFormDialog.vue';
import ZoneManagerDialog from './ZoneManagerDialog.vue';
import FloorMap from './FloorMap.vue';
import { NO_ZONE, ALL_ZONES, canvasOf, applyPending, prunePending, zoneTabs, floorSections } from '../../service/floorLayout';

const STATE_LABELS = { free: 'Libre', occupied: 'Ocupada', billing: 'Por cobrar', reserved: 'Reservada' };
// Evita refrescar el salón decenas de veces si llegan varios avisos seguidos (p.ej. mover una
// cuenta toca 2 mesas = 1 push, pero varias acciones rápidas de distintos meseros sí pueden
// solaparse). No aplica a las recargas que dispara el propio usuario (esas son instantáneas).
const FLOOR_PUSH_DEBOUNCE_MS = 300;
const VIEW_KEY = 'contablus.restaurant.view';   // preferencia por navegador: mapa o lista
const ZONE_KEY = 'contablus.restaurant.zone';   // última zona abierta

function readPref(key, fallback) {
    try { return window.localStorage.getItem(key) || fallback; } catch (e) { return fallback; }
}
function writePref(key, value) {
    try { window.localStorage.setItem(key, String(value)); } catch (e) { /* almacenamiento bloqueado */ }
}

export default {
    name: 'RestaurantFloor',
    components: { TableDetailPanel, ProductPickerDialog, TableFormDialog, ZoneManagerDialog, FloorMap },
    props: {
        // Documento del perfil de la pantalla de ventas (Capa 4) ya fusionado y filtrado por el servidor.
        // Solo PRESENTACIÓN: qué se muestra y cómo; el acceso lo deciden los permisos. Sin perfil, defaults.
        profile: { type: Object, default: () => ({}) },
    },
    data() {
        const savedZone = readPref(ZONE_KEY, null);
        return {
            floor: { tables: [], zones: [], canvas: null },
            loading: false,
            loaded: false,
            selectedZoneId: [null, NO_ZONE, ALL_ZONES].includes(savedZone) ? savedZone : Number(savedZone),
            selectedTableId: null,
            // Sin preferencia guardada, un teléfono abre en Lista (el plano necesita ancho para leerse).
            view: readPref(VIEW_KEY, window.innerWidth < 640 ? 'list' : 'map') === 'list' ? 'list' : 'map',
            viewOptions: [
                { value: 'map', label: 'Mapa', icon: 'pi pi-map' },
                { value: 'list', label: 'Lista', icon: 'pi pi-th-large' },
            ],
            manageMode: false,
            // Posiciones arrastradas y aún no guardadas: {tableId: {posX, posY}}
            pending: {},
            savingLayout: false,
            // Selector de productos (abrir cuenta / agregar ítems)
            pickerVisible: false,
            pickerMode: 'open',      // 'open' | 'add'
            pickerAccount: null,
            pickerSaving: false,
            // Config del salón
            tableFormVisible: false,
            editingTable: null,
            newTableZone: null,      // zona desde la que se pidió «Nueva mesa» (sección de «Todas»)
            zoneManagerVisible: false,
            // Tiempo real: avisos de otros meseros (App.vue -> EventBus)
            floorPushTimer: null,
        };
    },
    computed: {
        canManage() { return this.$can('restaurantTablesManage'); },
        activityName() {
            const a = this.$store.getters.defaultActivity;
            return (a && a.name) || 'Restaurante';
        },
        // Propiedades del perfil que esta pantalla consume (las marcadas renderedOn: web en el registro).
        // Ausente = encendido: el perfil apaga cosas, nunca inventa.
        showTotals() { return !(this.profile.tables && this.profile.tables.showTotals === false); },
        zonesEnabled() { return !(this.profile.tables && this.profile.tables.zonesEnabled === false); },
        sendToKitchen() { return !(this.profile.defaults && this.profile.defaults.sendToKitchen === false); },
        splitPayment() { return !(this.profile.defaults && this.profile.defaults.splitPayment === false); },
        canvas() { return canvasOf(this.floor); },
        tables() { return applyPending(this.floor.tables, this.pending); },
        tabs() { return zoneTabs(this.floor.zones, this.floor.tables); },
        // Solo las zonas reales (sin «Sin zona»), con su orden y conteo, para el gestor.
        tabsOfRealZones() {
            return this.floor.zones.map(z => ({ ...z, tableCount: this.floor.tables.filter(t => t.zoneId === z.id).length }));
        },
        // «Todas» va primero y solo tiene sentido con más de una zona.
        tabsWithAll() {
            if (this.tabs.length < 2) return this.tabs;
            return [{ id: ALL_ZONES, name: 'Todas', tableCount: this.floor.tables.length }, ...this.tabs];
        },
        // Sin preferencia guardada (o si la zona guardada ya no existe) abre en «Todas».
        currentZoneId() {
            const ids = this.tabsWithAll.map(z => z.id);
            // Zonas apagadas por el perfil: sin pestañas, todo el salón de corrido.
            if (!this.zonesEnabled && ids.includes(ALL_ZONES)) return ALL_ZONES;
            if (ids.includes(this.selectedZoneId)) return this.selectedZoneId;
            return ids.length ? ids[0] : null;
        },
        isAll() { return this.currentZoneId === ALL_ZONES; },
        sections() {
            return floorSections(this.tabs, this.tables, this.currentZoneId, { includeEmpty: this.manageMode });
        },
        // Zona por defecto de una mesa nueva: la de la sección donde se pidió, la abierta, o la primera.
        newTableZoneId() {
            if (typeof this.newTableZone === 'number') return this.newTableZone;
            if (typeof this.currentZoneId === 'number') return this.currentZoneId;
            return this.floor.zones.length ? this.floor.zones[0].id : null;
        },
        dirtyCount() { return Object.keys(this.pending).length; },
        dirty() { return this.dirtyCount > 0; },
        pickerTitle() {
            return this.pickerMode === 'open' ? 'Abrir nueva cuenta' : 'Agregar ítems a la cuenta';
        },
    },
    watch: {
        view(value) { writePref(VIEW_KEY, value); },
        currentZoneId(value) { if (value !== null) writePref(ZONE_KEY, value); },
    },
    mounted() {
        this.loadFloor();
        this._onFloorPush = (data) => this.onFloorPush(data);
        AppEventBus.on('restaurant_floor_changed', this._onFloorPush);
    },
    beforeUnmount() {
        AppEventBus.off('restaurant_floor_changed', this._onFloorPush);
        clearTimeout(this.floorPushTimer);
    },
    methods: {
        // Lo llama el guard de ruta del padre (SalonEntry): no salir con una distribución sin guardar.
        confirmLeave() {
            if (this.dirty) {
                return window.confirm('Hay cambios sin guardar en la distribución del salón. ¿Salir sin guardar?');
            }
            return true;
        },
        money(value) { return Number(value || 0).toFixed(2); },
        stateLabel(state) { return STATE_LABELS[state] || state; },
        async loadFloor() {
            this.loading = true;
            const res = await httpRequest.getRestaurantFloor();
            this.loading = false;
            if (res && res.status === 200) {
                this.floor = { tables: res.data.tables || [], zones: res.data.zones || [], canvas: res.data.canvas || null };
                this.loaded = true;
                // Lo arrastrado sin guardar sobrevive a la recarga (p.ej. un push en vivo), salvo mesas borradas.
                this.pending = prunePending(this.pending, this.floor.tables);
                if (this.selectedTableId && !this.floor.tables.some(t => t.id === this.selectedTableId)) {
                    this.selectedTableId = null;
                }
            } else {
                this.$toast.add({ severity: 'warn', summary: 'No se pudo cargar el salón', life: 4000 });
            }
        },
        // Tiempo real: alguien más (otro mesero, otro tab) cambió el salón. Filtra por actividad —
        // la presencia del servidor solo sabe de negocio, así que un negocio con varios locales
        // puede avisarnos de un cambio que no es el nuestro; lo descartamos acá.
        onFloorPush(data) {
            const activity = this.$store.getters.defaultActivity;
            if (!data || (activity && activity.id && data.activityId && data.activityId !== activity.id)) return;
            clearTimeout(this.floorPushTimer);
            this.floorPushTimer = setTimeout(() => {
                this.loadFloor();
                if (!this.selectedTableId) return;
                const deleted = data.deletedTableIds || [];
                const changed = data.tableIds || [];
                if (deleted.includes(this.selectedTableId)) {
                    // La mesa que tenías abierta ya no existe (la borraron desde «Configurar salón»).
                    this.selectedTableId = null;
                    this.$toast.add({ severity: 'info', summary: 'Esa mesa se eliminó', life: 3000 });
                } else if (changed.includes(this.selectedTableId) && this.$refs.panel) {
                    this.$refs.panel.reload();
                }
            }, FLOOR_PUSH_DEBOUNCE_MS);
        },
        onTileClick(table) {
            if (this.manageMode) {
                this.editingTable = table;
                this.tableFormVisible = true;
            } else {
                this.selectedTableId = table.id;
            }
        },
        // ── Configurar salón: arrastrar y guardar la distribución ─────────────
        startManage() {
            this.manageMode = true;
            this.selectedTableId = null;    // el panel de cuentas no aplica mientras se arma el salón
        },
        onDragEnd({ id, posX, posY }) {
            const original = this.floor.tables.find(t => t.id === id);
            const pending = { ...this.pending };
            if (original && original.posX === posX && original.posY === posY) delete pending[id];
            else pending[id] = { posX, posY };
            this.pending = pending;
        },
        discardLayout() {
            this.pending = {};
        },
        async finishManage() {
            if (this.dirty) {
                this.savingLayout = true;
                const positions = Object.entries(this.pending).map(([id, pos]) => ({ id: Number(id), ...pos }));
                const res = await httpRequest.saveRestaurantTableLayout(positions);
                this.savingLayout = false;
                if (!res || res.status !== 200) {
                    this.$toast.add({ severity: 'warn', summary: 'No se pudo guardar la distribución',
                                      detail: (res && res.error) || 'Intenta de nuevo.', life: 4000 });
                    return;   // sigue en modo edición con los cambios intactos
                }
                this.pending = {};
                this.$toast.add({ severity: 'success', summary: 'Distribución guardada', life: 2500 });
                await this.loadFloor();
            }
            this.manageMode = false;
        },
        // ── Abrir cuenta / agregar ítems ──────────────────────────────────────
        onOpenAccount() {
            this.pickerMode = 'open';
            this.pickerAccount = null;
            this.pickerVisible = true;
        },
        onAddItems(account) {
            this.pickerMode = 'add';
            this.pickerAccount = account;
            this.pickerVisible = true;
        },
        async onPickerConfirm({ label, items }) {
            this.pickerSaving = true;
            let res;
            if (this.pickerMode === 'open') {
                res = await httpRequest.openRestaurantAccount(this.selectedTableId, label, items);
            } else {
                res = await httpRequest.addRestaurantItems(this.pickerAccount.id, items);
            }
            this.pickerSaving = false;
            if (res && res.status === 200) {
                this.pickerVisible = false;
                const focusId = this.pickerMode === 'open' ? res.data.id : this.pickerAccount.id;
                if (this.$refs.panel) await this.$refs.panel.reload(focusId);
                this.loadFloor();
            } else {
                this.$toast.add({ severity: 'warn', summary: 'No se pudo guardar',
                                  detail: (res && res.error) || 'Intenta de nuevo.', life: 4000 });
            }
        },
        // ── Mesas (crear / editar / borrar) ───────────────────────────────────
        newTable(zoneId) {
            // Desde la barra llega el evento del click, no una zona.
            this.newTableZone = typeof zoneId === 'number' ? zoneId : null;
            this.editingTable = null;
            this.tableFormVisible = true;
        },
        onTableSaved(table) {
            // Ver la mesa donde quedó (en «Todas» ya se ve, no hace falta cambiar de pestaña).
            if (table && table.zoneId && !this.isAll) this.selectedZoneId = table.zoneId;
            this.loadFloor();
        },
        onTableDeleted(tableId) {
            if (this.selectedTableId === tableId) this.selectedTableId = null;
            this.loadFloor();
        },
    },
};
</script>

<style scoped>
.rt-topbar { display: flex; flex-direction: column; gap: 0.85rem; }
.rt-topbar-row { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; flex-wrap: wrap; }
.rt-title { display: flex; align-items: center; gap: 0.5rem; }
.rt-actions-top { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.rt-view-label { margin-left: 0.4rem; }
.rt-zones { display: flex; gap: 0.5rem; overflow-x: auto; padding-bottom: 2px; scrollbar-width: thin; }
.rt-zone { border: none; background: var(--surface-100); border-radius: 999px; padding: 0.45rem 1rem; cursor: pointer;
    font-weight: 600; color: var(--text-color-secondary); white-space: nowrap; font: inherit; }
.rt-zone:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; }
.rt-zone-on { background: var(--primary-color); color: var(--primary-color-text); }
.rt-zone-count { opacity: .7; font-size: .8em; margin-left: .25rem; }
.rt-manage-hint { display: flex; align-items: center; gap: .6rem; background: var(--surface-100); border-radius: 10px;
    padding: .6rem .85rem; margin-bottom: .75rem; font-size: .9rem; }
.rt-manage-hint i { color: var(--primary-color); }
.rt-legend { display: flex; gap: 1rem; margin: 0 0 0.85rem; flex-wrap: wrap; font-size: 0.85rem;
    color: var(--text-color-secondary); align-items: center; }
.rt-dot { width: 12px; height: 12px; border-radius: 50%; display: inline-block; margin-right: 0.25rem; vertical-align: middle; }
.rt-dot-free { background: #22c55e; } .rt-dot-occupied { background: #f97316; }
.rt-dot-billing { background: #1e40af; } .rt-dot-reserved { background: #9ca3af; }
.rt-bubble-sample { display: inline-flex; align-items: center; justify-content: center; width: 16px; height: 16px;
    border-radius: 999px; background: var(--primary-color); color: var(--primary-color-text); font-size: 10px;
    font-weight: 800; font-style: normal; margin-right: .25rem; }
.rt-section + .rt-section { margin-top: 1.25rem; }
.rt-section-title { display: inline-flex; align-items: center; gap: .5rem; border: none; background: none; padding: 0;
    margin-bottom: .5rem; font: inherit; font-weight: 700; font-size: 1rem; color: var(--text-color); cursor: pointer; }
.rt-section-title span { font-weight: 500; font-size: .82rem; color: var(--text-color-secondary); }
.rt-section-title i { color: var(--text-color-secondary); font-size: .85rem; }
.rt-section-title:hover i, .rt-section-title:focus-visible i { color: var(--primary-color); }
.rt-plane-empty { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center;
    justify-content: center; gap: .5rem; color: var(--text-color-secondary); }
.rt-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 1rem; }
.rt-tile { border: 2px solid transparent; border-radius: 14px; padding: 1rem; cursor: pointer; text-align: left;
    min-height: 120px; display: flex; flex-direction: column; justify-content: space-between; transition: transform .08s;
    font: inherit; }
.rt-tile:hover { transform: translateY(-2px); }
.rt-tile-selected { outline: 3px solid var(--primary-color); outline-offset: 1px; }
.rt-tile-manage { border-style: dashed !important; }
.rt-tile-top { display: flex; justify-content: space-between; align-items: flex-start; }
.rt-tile-name { font-weight: 800; font-size: 1.1rem; }
.rt-tile-badge { font-size: 0.65rem; font-weight: 800; text-transform: uppercase; letter-spacing: .03em;
    background: rgba(255,255,255,.6); border-radius: 6px; padding: 0.1rem 0.4rem; }
.rt-tile-edit { color: var(--text-color-secondary); }
.rt-tile-mid { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
.rt-tile-cap { font-size: 0.8rem; opacity: 0.8; }
.rt-tile-bubble { font-size: 0.7rem; font-weight: 700; background: rgba(255,255,255,.7); border-radius: 999px;
    padding: 0.1rem 0.5rem; }
.rt-tile-total { font-weight: 800; font-size: 1.15rem; }
.rt-floor-empty { display: flex; flex-direction: column; align-items: center; gap: 0.75rem; padding: 2.5rem;
    color: var(--text-color-secondary); text-align: center; }
.rt-state-free { background: #f0fdf4; color: #166534; border-color: #bbf7d0; }
.rt-state-occupied { background: #fff7ed; color: #9a3412; border-color: #fed7aa; }
.rt-state-billing { background: #eff6ff; color: #1e3a8a; border-color: #bfdbfe; }
.rt-state-reserved { background: #f3f4f6; color: #4b5563; border-color: #e5e7eb; }
@media (max-width: 640px) {
    .rt-view-label { display: none; }
}
</style>
